/**
 * Pure helpers for the AI Coach Knock in-app feed (SCO-371) and the producer
 * banner + bell surface. Kept free of the `@knocklabs/client` runtime so they
 * unit-test with plain objects.
 */
import { z } from 'zod';
/** Knock workflow key for the interactive morning check-in (matches the backend trigger). */
export var MORNING_CHECKIN_WORKFLOW_KEY = 'morning-checkin';
/**
 * The producer-stage ask (SCO-470, escalation ADR §1) — delivered TO the
 * producer, answered BY the producer. Distinct from the coach's
 * `morning-checkin`: a tap here is producer-confirmed attendance, which the
 * backend attributes by workflow key (`parseMorningCheckinInteraction` derives
 * `source: 'producer'`), not by who happens to be logged in.
 */
export var PRODUCER_MORNING_CHECKIN_WORKFLOW_KEY = 'producer-morning-checkin';
/** Rung-1 scoring nudge (SCO-504) — the one workflow whose tap NAVIGATES. */
export var PRODUCER_SCORING_NUDGE_WORKFLOW_KEY = 'producer-scoring-nudge';
/**
 * Workflows delivered TO producers (recipient = producer). `coach-nudge` is the
 * coach's one-tap send — despite the name, the producer receives it. Everything
 * else (morning-checkin, setup/weekend prompts, recaps) is coach/admin-facing
 * and must never surface on the producer banner or bell.
 */
export var PRODUCER_FACING_WORKFLOW_KEYS = [
    'producer-nudge',
    'coach-nudge',
    PRODUCER_MORNING_CHECKIN_WORKFLOW_KEY,
    // Rung-2 warning shot (SCO-505): the consequence-naming card between the
    // unanswered ask and the coach escalation.
    'producer-checkin-warning',
    // Rung-1 scoring nudge (SCO-504): present-but-silent, deep-linked to the
    // scoring screen. A nudge, not a ladder card — no roll-off entry.
    PRODUCER_SCORING_NUDGE_WORKFLOW_KEY
];
/**
 * Check-in surfaces the client day roll-off still governs. Owner ruling 2
 * (2026-08-11) removed the COACH-facing cards from this list: a coach-facing
 * question carries no clock and sits open until the coach answers it —
 * nothing (server sweep or client roll-off) self-resolves or hides it on the
 * coach's behalf. `morning-checkin` and `morning-checkin-digest` cards now
 * leave the feed only via an answer (`interacted_at`) or server archival
 * (`archived_at` — set when the row resolves, e.g. by the producer's own
 * activity). The rung-2 warning card stays: it is producer-facing and
 * informational, and stale "escalation is coming" copy must not outlive its
 * day.
 */
export var CHECKIN_ROLLOFF_WORKFLOW_KEYS = ['producer-checkin-warning'];
/** Knock names the in-app action_url block this; it is metadata, not body text. */
var ACTION_URL_BLOCK_NAME = 'action_url';
/** Whether a feed item came from the morning-checkin workflow (renders Yes/No). */
export function isMorningCheckinItem(item) {
    var _a;
    return ((_a = item.source) === null || _a === void 0 ? void 0 : _a.key) === MORNING_CHECKIN_WORKFLOW_KEY;
}
/**
 * Whether a feed item is the producer's own attendance ask. These render
 * Yes/No instead of the banner's tap-to-dismiss: an `acknowledged` tap is a
 * server-side no-op (`mapKnockActionToStatus` returns null for anything that
 * isn't yes/no), but it still sets `interacted_at` — which would clear the
 * banner and leave the producer no way to answer.
 */
export function isProducerCheckinItem(item) {
    var _a;
    return ((_a = item.source) === null || _a === void 0 ? void 0 : _a.key) === PRODUCER_MORNING_CHECKIN_WORKFLOW_KEY;
}
/** Whether the item is any check-in-ladder surface the day roll-off governs. */
export function isCheckinRolloffItem(item) {
    var _a;
    return CHECKIN_ROLLOFF_WORKFLOW_KEYS.includes((_a = item.source) === null || _a === void 0 ? void 0 : _a.key);
}
var insertedAtDate = function (item) { return new Date(item.inserted_at); };
var isSameLocalDay = function (a, b) { return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate(); };
/** 'YYYY-MM-DD' in the viewer's local timezone. */
var localDateString = function (d) {
    var pad = function (n) { return String(n).padStart(2, '0'); };
    return "".concat(d.getFullYear(), "-").concat(pad(d.getMonth() + 1), "-").concat(pad(d.getDate()));
};
/**
 * Trigger data is external input — parse, never trust (SCO-406 lesson).
 * Calendar round-trip, not just shape (Codex round 2): a patterned-but-bogus
 * value like 9999-99-99 must fall back to inserted_at, not survive forever.
 */
var CheckinTriggerDataSchema = z.object({
    local_date: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .refine(function (value) {
        var _a = value.split('-').map(Number), year = _a[0], month = _a[1], day = _a[2];
        var d = new Date(Date.UTC(year, month - 1, day));
        return (d.getUTCFullYear() === year &&
            d.getUTCMonth() === month - 1 &&
            d.getUTCDate() === day);
    }, 'not a real calendar date')
});
/**
 * Whether a check-in card is still within its ACCOUNT-local day (Codex round
 * 1, #407 P1). The card's lifecycle is keyed on the account's `local_date`
 * (carried in trigger data), not the viewer's clock — comparing `inserted_at`
 * in browser-local time loses the card early for a coach east of the account
 * and keeps it late for one west of it. The viewer's timezone is still the
 * only "now" the client has, so the rule is deliberately DROP-LATE: the card
 * survives until the viewer's own calendar has moved past `local_date` —
 * never disappearing while the account's day could still be live (a backstop
 * must not eat live cards; server archival owns precision). Items without
 * parseable trigger data fall back to inserted_at same-local-day.
 */
function isWithinCheckinDay(item, now) {
    var parsed = CheckinTriggerDataSchema.safeParse(item.data);
    if (parsed.success) {
        return localDateString(now) <= parsed.data.local_date;
    }
    return isSameLocalDay(insertedAtDate(item), now);
}
/**
 * Items the coach still needs to see/act on: not yet interacted with and not
 * archived. The feed surfaces un-interacted items only — both a UX choice and
 * the compliance posture (no retained history); once a coach answers (or
 * another channel does, first-response-wins), the item drops out.
 *
 * Owner ruling 2 (2026-08-11): coach-facing question cards have NO day
 * roll-off — an unanswered question sits open until the coach answers it,
 * however old. Only the producer-facing warning card keeps the day cut-off
 * (see {@link CHECKIN_ROLLOFF_WORKFLOW_KEYS}).
 */
export function selectActiveFeedItems(items, now) {
    return items.filter(function (item) { return item.interacted_at === null &&
        item.archived_at === null &&
        (!isCheckinRolloffItem(item) || isWithinCheckinDay(item, now)); });
}
/**
 * Whether a check-in card should render live Yes/No buttons: ALWAYS, while it
 * is visible.
 *
 * Owner ruling 2 (2026-08-11) removed the shot clock this function used to
 * mirror. The backend's coach-stage timeout sweep is gone — a coach-facing
 * question sits open until the coach answers it, and the recorder accepts a
 * human answer over a machine `timeout` (SCO-517) — so a 60-minute client
 * cut-off would now be the ONLY thing declaring expiry, i.e. exactly the
 * self-resolution the ruling forbids. The no-zombie-buttons rule is upheld
 * the other way around: the buttons are live because the backend genuinely
 * accepts the answer. The (item, now) signature is kept so call sites and a
 * future visibility rule stay stable.
 */
export function isCheckinInteractive(_item, _now) {
    return true;
}
/**
 * Whether an expired check-in may be dismissed: never — there is no expired
 * state anymore (Owner ruling 2; see {@link isCheckinInteractive}). A
 * check-in leaves a surface via an answer or server archival, not a dismiss.
 */
export function isCheckinDismissable(_item, _now) {
    return false;
}
/**
 * The displayable body of a feed item: Knock's server-rendered HTML for its
 * textual (markdown/text) blocks, joined.
 *
 * Reads `rendered` (the Liquid-applied output) — NOT `content`, which is the
 * raw, un-interpolated template; reading `content` is what surfaced literal
 * `{{ data.producer_name }}` in the coach feed (SCO-406). The `action_url`
 * block is also `type: "markdown"` but is metadata (it makes the item
 * clickable), so it is excluded — otherwise its raw `{{ vars.app_url }}/…`
 * leaked into the body. `button_set` blocks are rendered as our own Yes/No UI.
 *
 * The returned HTML must be sanitized at the injection site (DOMPurify in
 * CoachKnockFeed) before rendering — `rendered` can embed values like
 * `producer_name` that a salesperson controls but a coach views. Defensive
 * against a malformed/missing blocks array.
 */
export function feedItemBodyHtml(blocks) {
    if (blocks === void 0) { blocks = []; }
    return blocks
        .filter(function (block) {
        var isTextual = block.type === 'markdown' || block.type === 'text';
        return (isTextual &&
            block.name !== ACTION_URL_BLOCK_NAME &&
            Boolean(block.rendered));
    })
        .map(function (block) { return block.rendered; })
        .join('\n');
}
/**
 * Whether a feed item is the rung-1 scoring nudge (SCO-504) — the ONE
 * producer workflow whose tap navigates. Owner contract: the tap lands
 * directly on the scoring screen, never the app root. Other producer
 * workflows also carry an `action_url` block (producer-nudge and the
 * warning card point at the app root), but their tap still means "I heard
 * you" — do not generalize navigation onto them.
 */
export function isScoringNudgeItem(item) {
    var _a;
    return ((_a = item.source) === null || _a === void 0 ? void 0 : _a.key) === PRODUCER_SCORING_NUDGE_WORKFLOW_KEY;
}
/**
 * The validated http(s) action URL of a feed item, or null. The `action_url`
 * block is excluded from {@link feedItemBodyHtml} as metadata — this is its
 * consumer. `rendered` is Knock's Liquid-applied output and can arrive
 * markdown-wrapped, so tags are stripped before validation; anything that
 * doesn't parse as an absolute http(s) URL (a relative path, a
 * `javascript:` payload from a compromised template) is dropped rather than
 * rendered tappable.
 */
export function feedItemActionUrl(blocks) {
    var _a;
    if (blocks === void 0) { blocks = []; }
    var block = blocks.find(function (b) { return b.name === ACTION_URL_BLOCK_NAME; });
    var raw = ((_a = block === null || block === void 0 ? void 0 : block.rendered) !== null && _a !== void 0 ? _a : '').replace(/<[^>]+>/g, '').trim();
    if (raw === '') {
        return null;
    }
    try {
        var url = new URL(raw);
        return url.protocol === 'http:' || url.protocol === 'https:' ?
            url.toString() :
            null;
    }
    catch (_b) {
        return null;
    }
}
// --- Producer banner + bell selectors (launch requirement, locked 2026-07-02) ---
/** How long an AI Coach message stays visible in the producer bell. */
export var BELL_ROLLOFF_MS = 8 * 60 * 60 * 1000;
/** Whether a feed item was delivered TO a producer (banner/bell candidate). */
/**
 * Items a surface still owes a "shown" report for (SCO-702). Knock stamps
 * `seen_at` optimistically the moment `markAsSeen` is called, so filtering on
 * it is the whole dedupe: a second report of the same list is a no-op.
 */
export function selectUnseenItems(items) {
    return items.filter(function (item) { return item.seen_at === null; });
}
/** Items a surface still owes a "was on a visible screen" report for (SCO-702). */
export function selectUnreadItems(items) {
    return items.filter(function (item) { return item.read_at === null; });
}
export function isProducerFacingItem(item) {
    var _a;
    return PRODUCER_FACING_WORKFLOW_KEYS.includes((_a = item.source) === null || _a === void 0 ? void 0 : _a.key);
}
var insertedAtMs = function (item) { return Date.parse(item.inserted_at); };
/**
 * The bell's day-view: every producer-facing message from the last 8 hours,
 * acknowledged or not (same-day "what was said and when"), newest first. An
 * item exactly 8 hours old has rolled off. No retained history beyond the
 * window — deliberate product/compliance posture, same as the coach feed.
 */
export function selectBellItems(items, now) {
    var cutoffMs = now.getTime() - BELL_ROLLOFF_MS;
    return items
        .filter(function (item) { return isProducerFacingItem(item) &&
        item.archived_at === null &&
        insertedAtMs(item) > cutoffMs &&
        // A misconfigured workflow can deliver an item with no textual
        // body (e.g. only an action_url block) — without this guard it
        // banners as avatars + "Tap to dismiss" around an empty message.
        feedItemBodyHtml(item.blocks).trim() !== ''; })
        .sort(function (a, b) { return insertedAtMs(b) - insertedAtMs(a); });
}
/**
 * The item the persistent top banner shows: the NEWEST producer-facing item
 * inside the 8h window that has not been acknowledged from any surface
 * (first-acknowledgment-wins — `interacted_at` is server-side Knock state, so
 * a tap on the phone clears the desktop banner via the realtime store).
 * Null → no banner.
 *
 * The window matters: without it, yesterday's un-acknowledged pace nudge
 * ("12 to go!") would banner as fresh coaching the next morning — stale,
 * wrong, and against the locked "gone by tomorrow" intent. An un-heard nudge
 * simply expires with the window; the recap covers the day.
 */
export function selectBannerItem(items, now) {
    var _a;
    // Derived from the bell's day-view (already producer-filtered, windowed,
    // and sorted newest-first) so the two surfaces share ONE eligibility rule
    // and can never diverge; the banner is simply the newest un-heard item.
    return ((_a = selectBellItems(items, now).find(function (item) { return item.interacted_at === null; })) !== null && _a !== void 0 ? _a : null);
}
/**
 * Trigger `data` is external input (set by the backend, transported by Knock),
 * so it's parsed rather than trusted. Anything malformed degrades to nulls —
 * the banner falls back to initials, never throws.
 */
var FeedItemAvatarDataSchema = z.object({
    producer_avatar_url: z.string().nullable().optional(),
    coach_avatar_url: z.string().nullable().optional()
});
export function feedItemAvatars(item) {
    var _a, _b;
    var parsed = FeedItemAvatarDataSchema.safeParse(item.data);
    if (!parsed.success) {
        return { producerAvatarUrl: null, coachAvatarUrl: null };
    }
    return {
        producerAvatarUrl: (_a = parsed.data.producer_avatar_url) !== null && _a !== void 0 ? _a : null,
        coachAvatarUrl: (_b = parsed.data.coach_avatar_url) !== null && _b !== void 0 ? _b : null
    };
}
